import type { ReactElement } from 'react';

import { useAnchorable } from '../../hooks/useAnchorable';

import { BlueSky } from './components/Bluesky';
import { Instagram } from './components/Instagram';
import { InstagramReel } from './components/InstagramReel';
import { Stocktwits } from './components/Stocktwits';
import { TikTok } from './components/TikTok';
import { Twitter } from './components/Twitter';
import { Youtube } from './components/Youtube';

import type { TServiceEmbedProps } from './ServiceEmbed.types';

// Screenshots generated since BEE-21397 (merged 2026-06-19T16:17:21Z) draw their own card frame;
// older ones captured Instagram's frameless embed page. The filename carries the generation time.
const FRAMED_INSTAGRAM_SCREENSHOTS_SINCE = 1781885841;
const INSTAGRAM_SCREENSHOT_TIMESTAMP = /_(\d{10})_[0-9a-f]{8}\.jpe?g/;

const hasFramedInstagramScreenshot = (screenshotUrl?: string) =>
  Number(screenshotUrl?.match(INSTAGRAM_SCREENSHOT_TIMESTAMP)?.[1]) >= FRAMED_INSTAGRAM_SCREENSHOTS_SINCE;

const ServiceEmbed = ({ element }: TServiceEmbedProps): ReactElement | null => {
  const { service, data, url, screenshotUrl } = element.attrs || {};
  const { anchorAttributes } = useAnchorable(element.attrs);

  if (service === 'youtube') {
    // Email YouTube redirects link to {{live_url}}#<id>, so the node id is the scroll target when no custom anchor is set.
    return <Youtube embedUrl={data?.embed_url} anchorId={anchorAttributes?.id ?? element.attrs?.id} />;
  }

  if (service === 'twitter') {
    return (
      <Twitter
        tweetContent={data?.tweet_content}
        tweetUrl={data?.tweet_url || url}
        profileName={data?.profile_name}
        profileUsername={data?.profile_username}
        postedAt={data?.posted_at}
        screenshotUrl={screenshotUrl}
        anchorId={anchorAttributes?.id}
      />
    );
  }

  if (service === 'tiktok') {
    return (
      <TikTok
        videoId={data?.video_id}
        citeUrl={data?.cite_url || url}
        authorId={data?.author_id}
        title={data?.title}
        anchorId={anchorAttributes?.id}
      />
    );
  }

  if (service === 'bluesky') {
    return (
      <BlueSky
        oembedHtml={data?.oembed_html}
        threadPostUri={data?.thread_post_uri}
        threadPostCid={data?.thread_post_cid}
        threadContent={data?.thread_content}
        threadUrl={data?.thread_url}
        profileName={data?.profile_name}
        profileUsername={data?.profile_username}
        postedAt={data?.posted_at}
        anchorId={anchorAttributes?.id}
      />
    );
  }

  if (service === 'stocktwits') {
    return (
      <Stocktwits
        dataOrigin={data?.data_origin}
        messageId={data?.message_id}
        messageContent={data?.message_content}
        messageUrl={data?.message_url}
        profileName={data?.profile_name}
        profileUsername={data?.profile_username}
        postedAt={data?.posted_at}
        anchorId={anchorAttributes?.id}
      />
    );
  }

  if (service === 'instagram') {
    if (data?.is_reel) {
      return (
        <InstagramReel
          postUrl={data?.post_url || url}
          authorProfileImage={data?.author_profile_image}
          authorName={data?.author_name}
          displayFollowerCount={data?.display_follower_count}
          followersDisplay={data?.followers_display}
          thumbnailUrl={data?.thumbnail_url}
          reelsLogoImageUrl={data?.reels_logo_image_url}
          anchorId={anchorAttributes?.id}
          screenshotUrl={screenshotUrl}
          screenshotFramed={hasFramedInstagramScreenshot(screenshotUrl)}
        />
      );
    }

    return (
      <Instagram
        postUrl={data?.post_url || url}
        authorProfileImage={data?.author_profile_image}
        authorName={data?.author_name}
        displayFollowerCount={data?.display_follower_count}
        followersDisplay={data?.followers_display}
        thumbnailUrl={data?.thumbnail_url}
        description={data?.description}
        logoImageUrl={data?.logo_image_url}
        anchorId={anchorAttributes?.id}
        screenshotUrl={screenshotUrl}
        screenshotFramed={hasFramedInstagramScreenshot(screenshotUrl)}
      />
    );
  }

  return null;
};

export { ServiceEmbed };
